import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/Droplets Clash/images";

export default function DropletsClashPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="4D Droplets Clash"
          description="A speculative geometry study: how would a being from the 4D world perceive water droplets clashing inside a 3D water volume? Grasshopper’s magnetic-field simulator is used to capture the disturbance as a sequence of 3D waves."
          meta={["FA101 · Basic Design Studio", "3D water volume", "Rhino", "Grasshopper", "2016"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Card_mix_1c.png`}
                alt="4D Droplets Clash — concept composite"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Concept">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Water droplets falling on a 2D surface form clashing rings of waves — events we can read in a single plane. Translated into 3D, the same droplets would propagate as expanding shells through a body of water. The study asks how that volumetric disturbance would appear to an observer in a 4D world: not as rings, but as captured 3D geometries.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/1t.jpg`} alt="Disturbance study, view 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/2t.jpg`} alt="Disturbance study, view 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/3tt.jpg`} alt="Disturbance study, view 3" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/4t.jpg`} alt="Disturbance study, view 4" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Field simulation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Grasshopper’s magnetic-fields simulator stands in for the water medium. Each droplet is read as a charge that excites the field; the field’s isosurfaces are then captured as 3D geometries — the closest visual analogue to the disturbance a 4D observer would perceive.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/c1.jpg`} alt="Field iteration 01" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/c2.jpg`} alt="Field iteration 02" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/c3.jpg`} alt="Field iteration 03" ratio="square" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/c4.jpg`} alt="Field iteration 04" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/f1.jpg`} alt="Field iteration 05" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/f2.jpg`} alt="Field iteration 06" ratio="square" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Captured geometries">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Slicing through the simulated field at successive moments yields a family of solids — each one a snapshot of the volumetric clash, frozen in time.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/i1.jpg`} alt="Captured geometry, view 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/i2.jpg`} alt="Captured geometry, view 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/all.jpg`} alt="Captured geometries — family overview" ratio="banner" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Physical models">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Model Images/20161121_062135-1.jpg`} alt="Physical model, study 1" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Model Images/20161121_062157-1.jpg`} alt="Physical model, study 2" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Model Images/20161122_073717-1.jpg`} alt="Physical model, study 3" ratio="square" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Model Images/20161122_073749-1.jpg`} alt="Physical model, study 4" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Model Images/20161122_073824-1.jpg`} alt="Physical model, study 5" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Model Images/20161122_130810-1.jpg`} alt="Physical model, study 6" ratio="square" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
